import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Flag, Loader2, RefreshCw, AlertTriangle, Check, Plus, Copy, Trash2, Save,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import { apiService, ConfigInspection, ConfigSectionsQuery, ContainerApplyResult } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { ApplyReport, ContainerScope, NoReference } from '@/components/config/AllContainers';
import styles from './LootboxPage.module.scss';

// ============================================================
// Factions (faction.config.json), communes à tous les containers du projet
// ============================================================
// Structure : { ApiSettings, RabbitSettings, Factions, Claim, Technology: { Version, Branches:
// [ { Branch, Tiers: [ { Tier, ResearchDurationMs, MoneyCost, Conditions[], Rewards[] } ] } ] } }.
//
// Pas de choix de container : la page lit le fichier du container de référence (le premier, par
// id, qui le porte) et l'enregistrement écrit Factions, Claim et Technology dans CHAQUE container du
// projet qui le porte. Le bandeau dit lesquels en diffèrent aujourd'hui.
//
// ATTENTION : ApiSettings (URLs internes) et RabbitSettings (identifiants RabbitMQ) ne quittent
// jamais le container : le serveur ne renvoie et n'écrit que les trois sections éditées, le reste
// du document de chaque container reste le sien.
//
// Les formulaires Factions / Claim sont GÉNÉRÉS depuis les clés réellement présentes dans le
// fichier : le mod déployé porte des champs que son checkout local ne connaît pas encore
// (DefaultMembers, UserMaxRanks…), et une liste codée en dur les aurait masqués.
// ============================================================

/** Le seul fichier édité par cette page, avec ou sans extension .json. */
const FACTION_CONFIG = 'faction.config';

/** Sections gérées par la page ; le reste du fichier n'est ni lu ni écrit. */
const FACTION_KEYS = ['Factions', 'Claim', 'Technology'] as const;

const FACTION_QUERY: ConfigSectionsQuery = {
  fileNames: [`${FACTION_CONFIG}.json`, FACTION_CONFIG],
  keys: [...FACTION_KEYS],
};

const SECTION_LABELS: Record<string, string> = {
  Factions: 'règles des factions',
  Claim: 'règles de claim',
  Technology: 'arbre de technologies',
};

/** Sections jamais affichées : secrets et adresses internes, préservés tels quels. */
const HIDDEN_SECTIONS = ['ApiSettings', 'RabbitSettings'];

/** Types de condition observés dans les configs réelles ; un type inconnu reste éditable. */
const CONDITION_TYPES = [
  'DepositMoney', 'CraftItem', 'UseItem', 'BreakBlock', 'BreakBlockWithWither', 'KillEntity',
  'KillPlayers', 'SummonWither', 'WalkBlocksInTerritory', 'VisitFactionClaims', 'ClaimChunks',
  'MemberCount', 'StealBlocksWithHangGlider',
];
const TRACKING_MODES = ['Counter', 'DistinctSet'];
const REWARD_TYPES = ['UnlockCraft', 'UnlockUsage', 'UnlockFeature', 'SetMaxClaims', 'SetMaxMembers', 'SetMaxRoles'];

/** Les récompenses Set* portent une Value numérique, les Unlock* une Target texte. */
function rewardUsesValue(type: string | undefined): boolean {
  return !!type && type.startsWith('Set');
}

interface TechCondition {
  Key?: string;
  Type?: string;
  TrackingMode?: string;
  Required?: number;
  Item?: string;
  Description?: string;
  [k: string]: unknown;
}

interface TechReward {
  Type?: string;
  Target?: string;
  Value?: number;
  Description?: string;
  UiItemItemSettings?: { ItemIdentifier?: string; ItemCount?: number; [k: string]: unknown };
  [k: string]: unknown;
}

interface TechTier {
  Tier?: number;
  ResearchDurationMs?: number;
  MoneyCost?: number;
  Conditions?: TechCondition[];
  Rewards?: TechReward[];
  [k: string]: unknown;
}

interface TechBranch {
  Branch?: string;
  Tiers?: TechTier[];
  [k: string]: unknown;
}

interface Technology {
  Version?: number;
  Branches?: TechBranch[];
  [k: string]: unknown;
}

/** Ce que la page édite : Factions / Claim / Technology (le serveur ne renvoie rien d'autre). */
interface FactionFile {
  Factions?: Record<string, unknown>;
  Claim?: Record<string, unknown>;
  Technology?: Technology;
  [k: string]: unknown;
}

/** Libellés français des clés connues ; une clé inconnue affiche son nom brut. */
const FIELD_LABELS: Record<string, string> = {
  MaxMembers: 'Membres max',
  DefaultMembers: 'Membres par défaut',
  MaxClaims: 'Claims max',
  DefaultClaims: 'Claims par défaut',
  MaxAlliances: 'Alliances max',
  MaxFactionRanks: 'Grades max',
  DefaultFactionRanks: 'Grades par défaut',
  UserMaxRanks: 'Grades max par joueur',
  DefaultFactionHomes: 'Homes par défaut',
  DefaultClaimDamageReductionPercent: 'Réduction dégâts claim (%)',
  DefaultHomeTeleportWarmupSeconds: 'Warmup téléport home (s)',
  MaxFactionNameLength: 'Longueur max du nom',
  MinFactionNameLength: 'Longueur min du nom',
  MaxFactionDescriptionLength: 'Longueur max description',
  DefaultFlags: 'Flags par défaut',
  FactionNameBlacklist: 'Noms interdits',
  DefaultFactions: 'Factions par défaut',
  EnableClaiming: 'Claim activé',
  EnableAutoClaim: 'Auto-claim',
  EnableOverClaim: 'Overclaim',
  PowerPerClaim: 'Power par claim',
  BlacklistWorlds: 'Mondes interdits (claim)',
};

function numOrEmpty(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? String(v) : '';
}

/** Durée lisible d'un ResearchDurationMs : 7200000 -> « 2 h ». */
function fmtMs(ms: unknown): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return 'instantané';
  const totalMin = Math.round(ms / 60000);
  if (totalMin === 0) return 'moins d\'une minute';
  const j = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const mn = totalMin % 60;
  return [j ? `${j} j` : null, h ? `${h} h` : null, mn ? `${mn} min` : null].filter(Boolean).join(' ');
}

export function FactionsPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [inspection, setInspection] = useState<ConfigInspection | null>(null);
  const [doc, setDoc] = useState<FactionFile | null>(null);
  const [orig, setOrig] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [report, setReport] = useState<ContainerApplyResult | null>(null);
  const [tab, setTab] = useState<'params' | 'tech'>('params');
  const [branchIdx, setBranchIdx] = useState(0);
  const [tierIdx, setTierIdx] = useState(0);
  /** Dernière lecture lancée : une réponse plus ancienne (projet précédent) est ignorée. */
  const loadSeq = useRef(0);
  /**
   * Brouillons des champs « liste » (édités en texte séparé par des virgules). Sans eux, retaper
   * la valeur re-normaliserait la chaîne à chaque frappe et la virgule en cours de saisie
   * disparaîtrait sous le curseur.
   */
  const [listDrafts, setListDrafts] = useState<Record<string, string>>({});

  /**
   * Lit Factions / Claim / Technology dans le container de référence et l'alignement des autres.
   * Le fichier est CHERCHÉ côté serveur dans chaque container sur le suffixe de son chemin.
   * `keepEdit` ne rafraîchit que le bandeau, après un enregistrement.
   */
  const load = useCallback(async (keepEdit = false) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    if (!keepEdit) {
      setStatus(null);
      setReport(null);
      setDoc(null);
    }
    const r = await apiService.inspectContainerConfig(FACTION_QUERY);
    if (seq !== loadSeq.current) return;
    setLoading(false);
    setInspection(r.data ?? null);
    if (keepEdit) return;
    if (!r.data) { setStatus({ ok: false, msg: r.error ?? 'Lecture impossible.' }); return; }
    if (!r.data.referenceContainerId) return; // aucun container ne porte le fichier : dit plus bas
    const sections = (r.data.sections ?? {}) as FactionFile;
    if (!sections.Factions || typeof sections.Factions !== 'object') {
      setStatus({ ok: false, msg: `${r.data.referencePath ?? FACTION_CONFIG} ne contient pas de section « Factions ».` });
      return;
    }
    setDoc(sections);
    setOrig(JSON.stringify(sections));
    setListDrafts({});
    setBranchIdx(0);
    setTierIdx(0);
  }, []);

  useEffect(() => { void load(); }, [activeGameId, load]);

  /**
   * Clés des sections telles que CHARGÉES : la liste des champs vient du fichier d'origine et non
   * du document en cours d'édition, pour qu'un champ vidé (donc retiré du JSON) reste affiché au
   * lieu de disparaître du formulaire sous la souris.
   */
  const origSections = useMemo(() => {
    try {
      const p = JSON.parse(orig) as FactionFile;
      return { Factions: Object.keys(p.Factions ?? {}), Claim: Object.keys(p.Claim ?? {}) };
    } catch { return { Factions: [], Claim: [] }; }
  }, [orig]);

  /** Autres sections du fichier : propres à chaque container, jamais envoyées ici. */
  const otherKeys = inspection?.otherKeys ?? [];

  // ---- Édition générique des sections Factions / Claim --------------------------------------

  const setSectionField = (section: 'Factions' | 'Claim', key: string, value: unknown | undefined) => {
    setDoc((prev) => {
      if (!prev) return prev;
      const cur = { ...(prev[section] ?? {}) } as Record<string, unknown>;
      if (value === undefined) delete cur[key];
      else cur[key] = value;
      return { ...prev, [section]: cur };
    });
  };

  const renderSection = (section: 'Factions' | 'Claim', title: string) => {
    const obj = (doc?.[section] ?? {}) as Record<string, unknown>;
    const keys = origSections[section];
    if (keys.length === 0) return null;
    return (
      <div key={section}>
        <div className={styles.tableHeadJobs} style={{ gridTemplateColumns: '1fr' }}>
          <span>{title}</span>
        </div>
        <div className={styles.fieldsGrid} style={{ marginTop: 8, marginBottom: 16 }}>
          {keys.map((key) => {
            const v = obj[key];
            const origV = v;
            const label = FIELD_LABELS[key] ?? key;
            const draftKey = `${section}.${key}`;
            // Liste de chaînes : édition en texte séparé par des virgules.
            if (Array.isArray(origV) || listDrafts[draftKey] !== undefined) {
              const text = listDrafts[draftKey] ?? (Array.isArray(v) ? (v as unknown[]).join(', ') : '');
              return (
                <label key={key} className={styles.field} title={key}>
                  <span className={styles.label}>{label}</span>
                  <input className={styles.input} value={text}
                    placeholder="valeurs séparées par des virgules"
                    onChange={(e) => {
                      setListDrafts((d) => ({ ...d, [draftKey]: e.target.value }));
                      setSectionField(section, key,
                        e.target.value.split(',').map((s) => s.trim()).filter(Boolean));
                    }} />
                </label>
              );
            }
            if (typeof v === 'boolean') {
              return (
                <label key={key} className={styles.field} title={key}>
                  <span className={styles.label}>{label}</span>
                  <span className={styles.checkCell}>
                    <input type="checkbox" checked={v}
                      onChange={(e) => setSectionField(section, key, e.target.checked)} />
                  </span>
                </label>
              );
            }
            if (typeof v === 'number' || v === undefined) {
              return (
                <label key={key} className={styles.field} title={key}>
                  <span className={styles.label}>{label}</span>
                  <input className={styles.input} type="number" step="any"
                    value={numOrEmpty(v)}
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      if (raw === '') { setSectionField(section, key, undefined); return; }
                      const n = Number(raw);
                      if (Number.isFinite(n)) setSectionField(section, key, n);
                    }} />
                </label>
              );
            }
            if (typeof v === 'string') {
              return (
                <label key={key} className={styles.field} title={key}>
                  <span className={styles.label}>{label}</span>
                  <input className={styles.input} value={v}
                    onChange={(e) => setSectionField(section, key, e.target.value)} />
                </label>
              );
            }
            // Objet ou forme inattendue : préservé tel quel, dit explicitement.
            return (
              <label key={key} className={styles.field} title={key}>
                <span className={styles.label}>{label}</span>
                <span className={styles.muted}>préservé tel quel (structure non éditable ici)</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  // ---- Édition de l'arbre Technology ---------------------------------------------------------

  const branches: TechBranch[] = doc?.Technology?.Branches ?? [];
  const branch = branches[branchIdx];
  const tiers: TechTier[] = branch?.Tiers ?? [];
  const tier = tiers[Math.min(tierIdx, Math.max(0, tiers.length - 1))];
  const realTierIdx = Math.min(tierIdx, Math.max(0, tiers.length - 1));

  const updateTier = (patch: (t: TechTier) => TechTier) => {
    setDoc((prev) => {
      if (!prev?.Technology?.Branches) return prev;
      const nextBranches = [...prev.Technology.Branches];
      const b = { ...nextBranches[branchIdx] };
      const nextTiers = [...(b.Tiers ?? [])];
      if (!nextTiers[realTierIdx]) return prev;
      nextTiers[realTierIdx] = patch({ ...nextTiers[realTierIdx] });
      b.Tiers = nextTiers;
      nextBranches[branchIdx] = b;
      return { ...prev, Technology: { ...prev.Technology, Branches: nextBranches } };
    });
  };

  const mutateRow = <T extends TechCondition | TechReward>(
    listKey: 'Conditions' | 'Rewards', i: number, patch: (r: T) => T,
  ) => {
    updateTier((t) => {
      const list = [...((t[listKey] ?? []) as T[])];
      list[i] = patch({ ...list[i] });
      return { ...t, [listKey]: list };
    });
  };

  const moveRow = (listKey: 'Conditions' | 'Rewards', i: number, d: number) => {
    updateTier((t) => {
      const list = [...((t[listKey] ?? []) as unknown[])];
      const j = i + d;
      if (j < 0 || j >= list.length) return t;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...t, [listKey]: list };
    });
  };

  const duplicateRow = (listKey: 'Conditions' | 'Rewards', i: number) => {
    updateTier((t) => {
      const list = [...((t[listKey] ?? []) as unknown[])];
      list.splice(i + 1, 0, JSON.parse(JSON.stringify(list[i])));
      return { ...t, [listKey]: list };
    });
  };

  const removeRow = (listKey: 'Conditions' | 'Rewards', i: number) => {
    updateTier((t) => {
      const list = [...((t[listKey] ?? []) as unknown[])];
      list.splice(i, 1);
      return { ...t, [listKey]: list };
    });
  };

  const addCondition = () => updateTier((t) => ({
    ...t,
    Conditions: [...(t.Conditions ?? []), {
      Key: '', Type: 'DepositMoney', TrackingMode: 'Counter', Required: 1, Description: '',
    }],
  }));

  const addReward = () => updateTier((t) => ({
    ...t,
    Rewards: [...(t.Rewards ?? []), {
      Type: 'UnlockCraft', Target: '', Description: '',
      UiItemItemSettings: { ItemIdentifier: 'minecraft:stick', ItemCount: 1 },
    }],
  }));

  const addTier = () => {
    setDoc((prev) => {
      if (!prev?.Technology?.Branches) return prev;
      const nextBranches = [...prev.Technology.Branches];
      const b = { ...nextBranches[branchIdx] };
      const nextTiers = [...(b.Tiers ?? [])];
      const nextNum = nextTiers.reduce((m, t) => Math.max(m, t.Tier ?? 0), 0) + 1;
      nextTiers.push({ Tier: nextNum, ResearchDurationMs: 0, MoneyCost: 0, Conditions: [], Rewards: [] });
      b.Tiers = nextTiers;
      nextBranches[branchIdx] = b;
      setTierIdx(nextTiers.length - 1);
      return { ...prev, Technology: { ...prev.Technology, Branches: nextBranches } };
    });
  };

  const removeTier = () => {
    if (!tier) return;
    if (!window.confirm(`Supprimer le palier ${tier.Tier ?? realTierIdx + 1} de la branche ${branch?.Branch ?? ''} ?`)) return;
    setDoc((prev) => {
      if (!prev?.Technology?.Branches) return prev;
      const nextBranches = [...prev.Technology.Branches];
      const b = { ...nextBranches[branchIdx] };
      const nextTiers = [...(b.Tiers ?? [])];
      nextTiers.splice(realTierIdx, 1);
      b.Tiers = nextTiers;
      nextBranches[branchIdx] = b;
      return { ...prev, Technology: { ...prev.Technology, Branches: nextBranches } };
    });
    setTierIdx((i) => Math.max(0, i - 1));
  };

  /** Un select qui propose les valeurs connues SANS écraser une valeur inconnue du fichier. */
  const typeSelect = (value: string, known: string[], onChange: (v: string) => void) => (
    <select className={styles.input} value={value} onChange={(e) => onChange(e.target.value)}>
      {!known.includes(value) && value && <option value={value}>{value} (inconnu)</option>}
      {!value && <option value="">—</option>}
      {known.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );

  // ---- Sauvegarde -----------------------------------------------------------------------------

  const serialized = doc ? JSON.stringify(doc) : '';
  const dirty = !!doc && serialized !== orig;

  /**
   * Écrit les sections présentes (Factions, et Claim / Technology quand la référence les porte) dans
   * chaque container du projet qui porte faction.config. Une section absente de la référence n'est
   * jamais retirée ailleurs : la page ne sait pas l'éditer.
   */
  const save = async () => {
    if (!doc) return;
    setSaving(true);
    setStatus(null);
    setReport(null);
    const sections: Record<string, unknown> = {};
    for (const k of FACTION_KEYS) {
      if (doc[k] !== undefined) sections[k] = doc[k];
    }
    const res = await apiService.applyContainerConfig({ ...FACTION_QUERY, sections });
    setSaving(false);
    if (!res.data) {
      setStatus({ ok: false, msg: res.error ?? 'Enregistrement en échec' });
      return;
    }
    setReport(res.data);
    // Un container en échec garde la page « non enregistrée » : un nouvel essai réécrit partout.
    if (res.data.success) setOrig(serialized);
    void load(true);
  };

  const reload = () => {
    if (dirty && !window.confirm('Recharger faction.config abandonnera les modifications non enregistrées. Continuer ?')) return;
    void load();
  };

  /** Lecture faite, mais aucun container du projet ne porte le fichier : le pourquoi, container par container. */
  const missing = inspection && !inspection.referenceContainerId ? inspection : null;

  /**
   * Sections absentes de la référence : la page ne sait ni les créer ni les retirer (retirer un arbre
   * de technologies parce que le premier container n'en a pas serait destructeur), le bandeau le dit.
   */
  const untouched = FACTION_KEYS.filter((k) => !doc || doc[k] === undefined);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Factions</h1>
          <p className={styles.subtitle}>
            {tab === 'params'
              ? 'Règles des factions et du claim (faction.config.json), communes à tous les containers du projet. Les sections ApiSettings et RabbitSettings ne sont ni affichées ni modifiées.'
              : 'Arbre de technologies des factions : branches, paliers, conditions et récompenses, commun à tous les containers du projet.'}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.secondaryBtn} onClick={reload} disabled={loading}>
            {loading ? <Loader2 size={14} className={styles.spin} /> : <RefreshCw size={14} />} Rafraîchir
          </button>
          <button className={styles.primaryBtn} onClick={save} disabled={!dirty || saving}
            title="Écrit les règles et l'arbre de technologies dans tous les containers du projet qui portent faction.config">
            {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Enregistrer
          </button>
        </div>
      </header>

      <nav className={styles.tabBar}>
        <button type="button" className={`${styles.tabBtn} ${tab === 'params' ? styles.tabBtnOn : ''}`}
          onClick={() => setTab('params')}>Règles</button>
        <button type="button" className={`${styles.tabBtn} ${tab === 'tech' ? styles.tabBtnOn : ''}`}
          onClick={() => setTab('tech')}>Technologies</button>
      </nav>

      {inspection && (
        <ContainerScope entries={inspection.entries} referencePath={inspection.referencePath}
          labelOf={(k) => SECTION_LABELS[k] ?? k} untouched={untouched} />
      )}

      {status && (
        <div className={status.ok ? styles.ok : styles.error}>
          {status.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {status.msg}
        </div>
      )}

      {report && <ApplyReport result={report} onClose={() => setReport(null)} />}

      <section className={styles.detail}>
        {!doc ? (
          <div className={styles.detailEmpty}>
            {loading ? (
              <><Loader2 size={22} className={styles.spin} /><p>Ouverture de faction.config.json…</p></>
            ) : missing && missing.entries.length === 0 ? (
              <>
                <AlertTriangle size={22} />
                <p>{missing.message}</p>
                <p className={styles.muted}>Déclare les containers du projet dans Paramètres généraux → Containers.</p>
              </>
            ) : missing ? (
              <NoReference file="faction.config.json" entries={missing.entries} />
            ) : (
              <>
                <Flag size={26} />
                <p>faction.config.json n'a pas pu être ouvert.</p>
              </>
            )}
          </div>
        ) : tab === 'params' ? (
          <>
            {otherKeys.length > 0 && (
              <p className={styles.muted}>
                Sections propres à chaque container, ni affichées ni modifiées : {otherKeys.join(', ')}
                {HIDDEN_SECTIONS.some((s) => otherKeys.includes(s)) ? ' (secrets non affichés)' : ''}.
              </p>
            )}
            {renderSection('Factions', 'Factions')}
            {renderSection('Claim', 'Claim')}
            <div className={styles.footerActions}>
              <span className={styles.muted}>{dirty ? 'Modifications non enregistrées.' : 'Enregistré.'}</span>
            </div>
          </>
        ) : !branches.length ? (
          <div className={styles.detailEmpty}>
            <AlertTriangle size={22} />
            <p>Ce fichier ne contient pas de section <code>Technology</code>.</p>
          </div>
        ) : (
          <>
            <div className={styles.boxHead}>
              <label className={styles.field}>
                <span className={styles.label}>Branche</span>
                <select className={styles.input} value={branchIdx}
                  onChange={(e) => { setBranchIdx(Number(e.target.value)); setTierIdx(0); }}>
                  {branches.map((b, i) => (
                    <option key={i} value={i}>{b.Branch ?? `Branche ${i + 1}`}</option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Palier</span>
                <select className={styles.input} value={realTierIdx}
                  onChange={(e) => setTierIdx(Number(e.target.value))}>
                  {tiers.map((t, i) => (
                    <option key={i} value={i}>Palier {t.Tier ?? i + 1}</option>
                  ))}
                </select>
              </label>
              <div className={styles.totals}>
                <span><strong>{tiers.length}</strong> palier(s)</span>
                <button className={styles.secondaryBtn} onClick={addTier}><Plus size={13} /> Palier</button>
                <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Supprimer ce palier"
                  onClick={removeTier} disabled={!tier}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>

            {tier && (
              <>
                <div className={styles.boxHead}>
                  <label className={styles.field}>
                    <span className={styles.label}>Durée de recherche (ms)</span>
                    <input className={styles.input} type="number" min={0}
                      value={numOrEmpty(tier.ResearchDurationMs)}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        updateTier((t) => ({ ...t, ResearchDurationMs: Number.isFinite(n) ? n : 0 }));
                      }} />
                  </label>
                  <label className={styles.field}>
                    <span className={styles.label}>Soit</span>
                    <span className={styles.muted}>{fmtMs(tier.ResearchDurationMs)}</span>
                  </label>
                  <label className={styles.field}>
                    <span className={styles.label}>Coût (argent)</span>
                    <input className={styles.input} type="number" min={0}
                      value={numOrEmpty(tier.MoneyCost)}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        updateTier((t) => ({ ...t, MoneyCost: Number.isFinite(n) ? n : 0 }));
                      }} />
                  </label>
                </div>

                <div className={styles.tableHeadTech}>
                  <span>Clé</span>
                  <span>Type</span>
                  <span>Mode</span>
                  <span>Objectif</span>
                  <span>Item</span>
                  <span>Description</span>
                  <span />
                </div>
                {(tier.Conditions ?? []).length === 0 && (
                  <p className={styles.muted}>Aucune condition : le palier se débloque par la seule durée de recherche.</p>
                )}
                {(tier.Conditions ?? []).map((c, i) => (
                  <div key={i} className={styles.rowEdit}>
                    <div className={styles.cellsTech}>
                      <span>
                        <input className={styles.input} value={c.Key ?? ''} placeholder="deposit_money"
                          onChange={(e) => mutateRow<TechCondition>('Conditions', i, (x) => ({ ...x, Key: e.target.value }))} />
                      </span>
                      <span>
                        {typeSelect(c.Type ?? '', CONDITION_TYPES,
                          (v) => mutateRow<TechCondition>('Conditions', i, (x) => ({ ...x, Type: v })))}
                      </span>
                      <span>
                        {typeSelect(c.TrackingMode ?? '', TRACKING_MODES,
                          (v) => mutateRow<TechCondition>('Conditions', i, (x) => ({ ...x, TrackingMode: v })))}
                      </span>
                      <span>
                        <input className={styles.input} type="number" min={0} value={numOrEmpty(c.Required)}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            mutateRow<TechCondition>('Conditions', i, (x) => ({ ...x, Required: Number.isFinite(n) ? n : 0 }));
                          }} />
                      </span>
                      <span>
                        <input className={styles.input} value={c.Item ?? ''} placeholder="minecraft:obsidian"
                          onChange={(e) => mutateRow<TechCondition>('Conditions', i, (x) => {
                            // Un Item vide n'est pas écrit : la plupart des types n'en portent pas.
                            const out: TechCondition = { ...x };
                            if (e.target.value.trim()) out.Item = e.target.value;
                            else delete out.Item;
                            return out;
                          })} />
                      </span>
                      <span>
                        <input className={styles.input} value={c.Description ?? ''}
                          onChange={(e) => mutateRow<TechCondition>('Conditions', i, (x) => ({ ...x, Description: e.target.value }))} />
                      </span>
                      <span className={styles.rowActions}>
                        <button className={styles.iconBtn} title="Monter" onClick={() => moveRow('Conditions', i, -1)}><ChevronUp size={13} /></button>
                        <button className={styles.iconBtn} title="Descendre" onClick={() => moveRow('Conditions', i, 1)}><ChevronDown size={13} /></button>
                        <button className={styles.iconBtn} title="Dupliquer" onClick={() => duplicateRow('Conditions', i)}><Copy size={13} /></button>
                        <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Supprimer" onClick={() => removeRow('Conditions', i)}><Trash2 size={13} /></button>
                      </span>
                    </div>
                  </div>
                ))}

                <div className={styles.footerActions}>
                  <button className={styles.secondaryBtn} onClick={addCondition}>
                    <Plus size={14} /> Ajouter une condition
                  </button>
                </div>

                <div className={styles.tableHeadReward}>
                  <span>Type</span>
                  <span>Cible</span>
                  <span>Valeur</span>
                  <span>Description</span>
                  <span>Icône UI</span>
                  <span>Qté</span>
                  <span />
                </div>
                {(tier.Rewards ?? []).length === 0 && <p className={styles.muted}>Aucune récompense sur ce palier.</p>}
                {(tier.Rewards ?? []).map((r, i) => {
                  const usesValue = rewardUsesValue(r.Type);
                  const known = REWARD_TYPES.includes(r.Type ?? '');
                  return (
                    <div key={i} className={styles.rowEdit}>
                      <div className={styles.cellsReward}>
                        <span>
                          {typeSelect(r.Type ?? '', REWARD_TYPES,
                            (v) => mutateRow<TechReward>('Rewards', i, (x) => ({ ...x, Type: v })))}
                        </span>
                        <span>
                          <input className={styles.input} value={r.Target ?? ''}
                            disabled={known && usesValue}
                            placeholder={usesValue ? '—' : 'hood_helmet'}
                            onChange={(e) => mutateRow<TechReward>('Rewards', i, (x) => ({ ...x, Target: e.target.value }))} />
                        </span>
                        <span>
                          <input className={styles.input} type="number" value={numOrEmpty(r.Value)}
                            disabled={known && !usesValue}
                            onChange={(e) => {
                              const raw = e.target.value.trim();
                              mutateRow<TechReward>('Rewards', i, (x) => {
                                const out = { ...x };
                                if (raw === '') delete out.Value;
                                else {
                                  const n = Number(raw);
                                  if (Number.isFinite(n)) out.Value = n;
                                }
                                return out;
                              });
                            }} />
                        </span>
                        <span>
                          <input className={styles.input} value={r.Description ?? ''}
                            onChange={(e) => mutateRow<TechReward>('Rewards', i, (x) => ({ ...x, Description: e.target.value }))} />
                        </span>
                        <span>
                          <input className={styles.input} value={r.UiItemItemSettings?.ItemIdentifier ?? ''}
                            placeholder="minecraft:stick"
                            onChange={(e) => mutateRow<TechReward>('Rewards', i, (x) => ({
                              ...x,
                              UiItemItemSettings: { ...(x.UiItemItemSettings ?? {}), ItemIdentifier: e.target.value },
                            }))} />
                        </span>
                        <span>
                          <input className={styles.input} type="number" min={1}
                            value={numOrEmpty(r.UiItemItemSettings?.ItemCount)}
                            onChange={(e) => {
                              const n = Number(e.target.value);
                              mutateRow<TechReward>('Rewards', i, (x) => ({
                                ...x,
                                UiItemItemSettings: { ...(x.UiItemItemSettings ?? {}), ItemCount: Number.isFinite(n) ? n : 1 },
                              }));
                            }} />
                        </span>
                        <span className={styles.rowActions}>
                          <button className={styles.iconBtn} title="Monter" onClick={() => moveRow('Rewards', i, -1)}><ChevronUp size={13} /></button>
                          <button className={styles.iconBtn} title="Descendre" onClick={() => moveRow('Rewards', i, 1)}><ChevronDown size={13} /></button>
                          <button className={styles.iconBtn} title="Dupliquer" onClick={() => duplicateRow('Rewards', i)}><Copy size={13} /></button>
                          <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Supprimer" onClick={() => removeRow('Rewards', i)}><Trash2 size={13} /></button>
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div className={styles.footerActions}>
                  <button className={styles.secondaryBtn} onClick={addReward}>
                    <Plus size={14} /> Ajouter une récompense
                  </button>
                  <span className={styles.muted}>{dirty ? 'Modifications non enregistrées.' : 'Enregistré.'}</span>
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
